import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginRedirectService } from '../services/login-redirect.service';
import { SimpleDialogService } from '../services/simple-dialog.service';
import { WINDOW } from '../tokens/window';

export const SKIP_ERROR_DIALOG = new HttpContextToken<boolean>(() => false);

enum ErrorCode {
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  OBJECT_NOT_FOUND = 'OBJECT_NOT_FOUND',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  NOT_VALID = 'NOT_VALID',
  INVALID_QUERY = 'INVALID_QUERY',
  PARSE_EXCEPTION = 'PARSE_EXCEPTION',
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private errorPopupCreated: boolean;

  constructor(
    private simpleDialogService: SimpleDialogService,
    private translateService: TranslateService,
    private loginRedirectService: LoginRedirectService,
    @Inject(WINDOW) private window: Window,
  ) {
    this.errorPopupCreated = false;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.loginRedirectService.exitToLoginScreen(true);
          } else if (!this.errorPopupCreated && !req.context.get(SKIP_ERROR_DIALOG)) {
            let message: string;
            let descr = '';
            if (error.error != null) {
              const errorCode = error.error.error;
              if (typeof error.error === 'string') {
                message = error.error;
              } else if (errorCode != null) {
                message = this.getErrorLocalization(errorCode);
                descr = error.error.descr;
              } else {
                message = error.statusText;
              }
            } else {
              message = error.statusText;
            }
            this.errorPopupCreated = true;
            this.simpleDialogService.createDialog({
              theme: 'error-theme',
              onClose: () => this.onPopupClose(),
              title: this.translateService.stream('httpError.title'),
              message: this.translateService.stream(
                'httpError.message',
                { message, descr, status: error.status },
              ),
              buttons: [
                {
                  label: this.translateService.stream('action.ok'),
                  onClick: () => this.onPopupClose(),
                },
              ],
            });
          }
        }
        return throwError(error);
      }),
    );
  }

  private onPopupClose(): Observable<boolean> {
    this.errorPopupCreated = false;
    return of(true);
  }

  private getErrorLocalization(error: ErrorCode): string {
    switch (error) {
      case ErrorCode.PROCESSING_ERROR:
        return this.translateService.instant('httpError.errorCode.PROCESSING_ERROR');
      case ErrorCode.OBJECT_NOT_FOUND:
        return this.translateService.instant('httpError.errorCode.OBJECT_NOT_FOUND');
      case ErrorCode.NOT_AUTHORIZED:
        return this.translateService.instant('httpError.errorCode.NOT_AUTHORIZED');
      case ErrorCode.NOT_AUTHENTICATED:
        return this.translateService.instant('httpError.errorCode.NOT_AUTHENTICATED');
      case ErrorCode.NOT_VALID:
        return this.translateService.instant('httpError.errorCode.NOT_VALID');
      case ErrorCode.INVALID_QUERY:
        return this.translateService.instant('httpError.errorCode.INVALID_QUERY');
      case ErrorCode.PARSE_EXCEPTION:
        return this.translateService.instant('httpError.errorCode.PARSE_EXCEPTION');
      default:
        return this.getErrorLocalization(ErrorCode.PROCESSING_ERROR);
    }
  }
}
